import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import get from 'lodash/get';
import map from 'lodash/map';
import set from 'lodash/set';

import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import NodeSelector from '../NodeSelector';
import useOptionsGeneratedIds from 'src/presentation/components/pages/ivr/hooks/useOptionsGeneratedIds';
import {
  ContainerForOptions,
  Content,
  Footer,
  Row,
  ScrollRow,
  StyledTextarea,
} from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import {
  NodeProps,
  SIDE_PANEL_ROW_TYPES,
  DESCRIPTION_PLACEHOLDER_TEXT,
  DESCRIPTION_LABEL_TEXT,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import { OptionContainer } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';

export const EXPRESSION_DESCRIPTION =
  'Dynamic configuration allows for data driven IVR logic, done in go template language';

const VariableMatchNode: FC<NodeProps> = ({
  attr,
  disabled,
  nodeList,
  onSelectNode,
  readOnly,
  selectedOptionIndex,
}) => {
  const { workingState, updateState } = useContext(FormContext);
  const [selectedIndex, setSelectedIndex] = useState(selectedOptionIndex);
  const variableMatches = get(workingState, `${attr}.variableMatches`, []);
  const currentNodeId = get(workingState, `${attr}.id`);

  const items = get(workingState, `${attr}.variableMatches`, []);

  const { addOptionId, optionsIds, removeOptionIdByIndex } = useOptionsGeneratedIds(items);

  useEffect(() => {
    setSelectedIndex(selectedOptionIndex);
  }, [selectedOptionIndex]);

  const onAddButtonClick = useCallback(() => {
    const newVariableMatches = [...variableMatches, { expression: '', pattern: '', nextId: null }];
    updateState(set({}, `${attr}.variableMatches`, newVariableMatches));
    setSelectedIndex(newVariableMatches.length - 1);
    addOptionId();
  }, [addOptionId, attr, updateState, variableMatches]);

  const renderedVariableMatches = (
    <ContainerForOptions
      data-aid={`${attr}-variableMatches`}
      label="Variable Matches"
      addButtonLabel="Add Variable Match"
      onAddButtonClick={onAddButtonClick}
      context={map(variableMatches, (variable, idx: number) => {
        const baseAttr = `${attr}.variableMatches[${idx}]`;
        const baseDataAid = `${attr}-variableMatches[${idx}]`;
        return (
          <OptionContainer
            attrList={`${attr}.variableMatches`}
            currentIndex={idx}
            data-aid={baseDataAid}
            disabled={disabled}
            id={baseDataAid}
            key={`${attr}-${optionsIds[idx]}`}
            readOnly={readOnly}
            removeOptionIdByIndex={removeOptionIdByIndex}
            selectedIndex={selectedIndex}
          >
            <Row>
              <StyledTextarea
                attr={`${baseAttr}.expression`}
                data-aid={`${baseDataAid}-expression`}
                description={EXPRESSION_DESCRIPTION}
                disabled={disabled}
                label="Expression"
                placeholder="Please enter a valid Go-template"
                readOnly={readOnly}
              />
            </Row>

            <Row>
              <StyledTextarea
                attr={`${baseAttr}.pattern`}
                data-aid={`${baseDataAid}-pattern`}
                description="A regular expression"
                disabled={disabled}
                label="Pattern"
                placeholder="Regular expression"
                readOnly={readOnly}
              />
            </Row>

            <Row>
              <NodeSelector
                attr={`${baseAttr}.nextId`}
                data-aid={`${baseDataAid}-nextId`}
                description="If the customer matches the dial or speech, they will go to this next node"
                disabled={disabled}
                label="Next Node"
                omittedNodeIds={[currentNodeId]}
                onSelectNode={onSelectNode}
                options={nodeList}
                readOnly={readOnly}
                searchable
              />
            </Row>
          </OptionContainer>
        );
      })}
    />
  );

  return (
    <>
      <Content>
        <Row>
          <InputField
            attr={`${attr}.name`}
            data-aid={`${attr}-name`}
            description="The name you can refer to link to other nodes"
            disabled={disabled}
            label="Name"
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <InputField
            attr={`${attr}.description`}
            data-aid={`${attr}-description`}
            disabled={disabled}
            label={DESCRIPTION_LABEL_TEXT}
            placeholder={DESCRIPTION_PLACEHOLDER_TEXT}
            readOnly={readOnly}
          />
        </Row>

        {renderedVariableMatches}
      </Content>

      <Footer>
        <ScrollRow scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.NOT_MATCH_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.defaultNextId`}
            disabled={disabled}
            data-aid={`${attr}-defaultNextId`}
            label="Default Next Node"
            description="The next node in the IVR"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </ScrollRow>
        <ScrollRow scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.ERROR_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.errorNodeId`}
            data-aid={`${attr}-errorNodeId`}
            disabled={disabled}
            label="Error Node"
            description="Next IVR node if an error is encountered while evaluating variable matches"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </ScrollRow>
      </Footer>
    </>
  );
};

export default VariableMatchNode;
